import { useEffect, useMemo, useState } from 'react';
import { Box, Button, List, ListItem, Theme, Typography, useMediaQuery } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import classnames from 'classnames';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import { CheckIcon } from 'assets';
import {
  PLAN_CODE_YEARLY,
  PLAN_CODE_MONTHLY,
  PLAN_CODE_TRIAL,
  SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE,
} from 'constants/payment';
import { SECOND_ACCOUNT_PAYMENT_REQUIRED } from 'constants/index';
import { BackButton, DiscountInput, Info, SeatsCountSelector, Spinner } from 'components';
import { IApiRequests, useApiRequests } from 'hooks';
import {
  ApiPlan,
  ApiUser,
  ComputeSubscriptionProrationMode,
  ComputeSubscriptionProrationResponse,
  Coupon,
  CreateHostedPageResponse,
  GetPlanResponse,
  SubscriptionProrationApi,
} from 'types';
import { useAppSelector } from 'store';
import { selectUser } from 'store/user';
import { getIntlLocale } from 'utils';
import { isHipaaLink } from 'config/productProfile';

import styles from './SubscriptionDetailsPage.module.scss';

export function SubscriptionDetailsPage() {
  const { t, i18n } = useTranslation('auth');
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  const { getPlan, createSubscriptionWithHostedPages, computeSubscriptionProration }: IApiRequests = useApiRequests();
  const navigate = useNavigate();

  const user: ApiUser = useAppSelector(selectUser)!;

  const [loading, setLoading] = useState<boolean>(true);
  const [plans, setPlans] = useState<ApiPlan[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string>(PLAN_CODE_MONTHLY);
  const [seatsCount, setSeatsCount] = useState<number>(1);
  const [subscriptionProration, setSubscriptionProration] = useState<SubscriptionProrationApi | null>(null);
  const [coupone, setCoupone] = useState<Coupon | null>(null);
  const [isBenefitsOpen, setIsBenefitsOpen] = useState<boolean>(false);
  const secondAccountPaymentRequired = localStorage.getItem(SECOND_ACCOUNT_PAYMENT_REQUIRED) === 'true';
  const locale = getIntlLocale(i18n.resolvedLanguage || i18n.language || 'en');

  useEffect(() => {
    if (
      !secondAccountPaymentRequired &&
      (!isMobile || user.clinicUser?.subscriptionInfo?.planCode !== PLAN_CODE_TRIAL)
    ) {
      navigate('/');
      return;
    }

    Promise.all([getPlan(PLAN_CODE_MONTHLY), getPlan(PLAN_CODE_YEARLY)])
      .then((value: [GetPlanResponse, GetPlanResponse]) => {
        setPlans(value.map((planResponse: GetPlanResponse) => planResponse.plan));
      })
      .catch(() => {
        enqueueSnackbar(t('plansFetchError'), { variant: 'error' });
        navigate('/');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setLoading(true);

    computeSubscriptionProration({
      plan: { code: selectedPlan, quantity: seatsCount },
      mode: ComputeSubscriptionProrationMode.ProrateNewSubscription,
      couponCode: coupone?.code,
    })
      .then((response: ComputeSubscriptionProrationResponse) => {
        setSubscriptionProration(response.subscriptionProration);
      })
      .catch((error) => {
        enqueueSnackbar(`${t('calculationErrorPrefix')}${error.message}`, { variant: 'error' });
        setSubscriptionProration(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [seatsCount, coupone, selectedPlan]);

  const pricePerOneSeatPerMonth: string = useMemo(() => {
    if (selectedPlan === PLAN_CODE_MONTHLY) {
      return plans[0]?.recurringPrice?.toFixed(2) ?? '';
    }

    return ((plans[1]?.recurringPrice ?? 0) / 12).toFixed(2);
  }, [selectedPlan, plans]);

  const calculationsSum: string = useMemo(() => {
    if (selectedPlan === PLAN_CODE_MONTHLY) {
      return ((plans[0]?.recurringPrice ?? 0) * seatsCount).toFixed(2);
    }

    return ((plans[1]?.recurringPrice ?? 0) * seatsCount).toFixed(2);
  }, [selectedPlan, plans, seatsCount]);

  const renewsDate: string = useMemo(() => {
    const date =
      selectedPlan === PLAN_CODE_MONTHLY
        ? moment(user.clinicUser.subscriptionInfo?.serverTime).add(1, 'M').toDate()
        : moment(user.clinicUser.subscriptionInfo?.serverTime).add(1, 'y').toDate();

    return t('renewsOnDate', {
      date: new Intl.DateTimeFormat(locale, { month: 'long', day: 'numeric', year: 'numeric' }).format(date),
    });
  }, [locale, selectedPlan, t, user.clinicUser.subscriptionInfo?.serverTime]);

  const benefitItems = useMemo(() => {
    return Array.from({ length: 13 }, (_, index) => t(`benefitItem${index + 1}`));
  }, [t]);

  const handleMonthlyClick = (): void => {
    if (selectedPlan !== PLAN_CODE_MONTHLY) {
      setCoupone(null);
      setSelectedPlan(PLAN_CODE_MONTHLY);
    }
  };

  const handleYearlyClick = (): void => {
    if (selectedPlan !== PLAN_CODE_YEARLY) {
      setCoupone(null);
      setSelectedPlan(PLAN_CODE_YEARLY);
    }
  };

  const handleDecreaseSeat = (): void => {
    if (seatsCount > 1) {
      setSeatsCount(seatsCount - 1);
    }
  };

  const handleIncreaseSeat = (): void => {
    setSeatsCount(seatsCount + 1);
  };

  const handleApplyCoupon = (coupon: Coupon): void => {
    setCoupone(coupon);
  };

  const handleRemoveCoupon = (): void => {
    setCoupone(null);
  };

  const handlePayClick = (): void => {
    if (coupone?.code === 'LIMITED50' && seatsCount < 10) {
      enqueueSnackbar(t('limited50MinSeatsError'), { variant: 'error' });
      return;
    }

    setLoading(true);

    createSubscriptionWithHostedPages({
      planCode: selectedPlan,
      quantity: seatsCount,
      couponCode: coupone?.code,
      redirectUrl: `${process.env.REACT_APP_PUBLIC_URI}/successPayment?type=${SUCCESS_OPERATION_ACTION_TYPE_SUBSCRIPTION_CHANGE}&redirectTo=settings&redirectToHash=subscriptions`,
    })
      .then((response: CreateHostedPageResponse) => {
        window.location.href = response.page.url;
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleBackToSubscriptionsClick = (): void => {
    navigate('/settings#subscriptions');
  };

  const handleBenefitsClick = (): void => {
    setIsBenefitsOpen(true);
  };

  const handleBenefitsClose = (): void => {
    setIsBenefitsOpen(false);
  };

  const benefitsPanel = (
    <Box className={classnames(styles.container, styles.containerBenefits)}>
      {isMobile && (
        <Typography variant='body4' component='span' color='primary' onClick={handleBenefitsClose}>
          {t('selectPlanGoBack')}
        </Typography>
      )}

      <Typography variant='h2' component='h2'>
        <>
          {t('benefitsOfHipaa')}
          <br />
          {isHipaaLink ? (
            <>
              HIPAA<span className={styles.link}>LINK</span>
            </>
          ) : (
            <>
              <strong>VIA3</strong>
            </>
          )}
        </>
      </Typography>

      <List>
        {benefitItems.map((item: string, key: number) => (
          <ListItem key={key}>
            <CheckIcon />
            <Typography variant='body2' component='span'>
              {item}
            </Typography>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  const detailsPanel = (
    <Box className={styles.container}>
      {(!secondAccountPaymentRequired || isMobile) && (
        <Typography variant='body2' component='span' onClick={handleBenefitsClick}>
          {t('viewSubscriptionBenefits')}
        </Typography>
      )}

      <div className={styles.switcher}>
        <div
          onClick={handleMonthlyClick}
          className={classnames(styles.switcherItem, {
            [styles.switcherItemActive]: selectedPlan === PLAN_CODE_MONTHLY,
          })}
        >
          <Typography variant='body5' component='span'>
            {t('monthly')}
          </Typography>
        </div>

        <div
          onClick={handleYearlyClick}
          className={classnames(styles.switcherItem, {
            [styles.switcherItemActive]: selectedPlan === PLAN_CODE_YEARLY,
          })}
        >
          <Typography variant='body5' component='span'>
            {t('annually')}
          </Typography>
        </div>

        <div className={styles.discount}>
          <Typography variant='body' component='span'>
            {t('off20')}
          </Typography>
        </div>
      </div>

      <div className={classnames(styles.row, styles.pricePerMonth)}>
        <Typography variant='h4' component='h4'>
          {t('monthSeatRate', { price: pricePerOneSeatPerMonth })}
        </Typography>

        <Info text={t('seatsInfo')} tooltipClassName={styles.tooltip} />

        <Typography variant='body2' component='span' color='#626363'>
          {selectedPlan === PLAN_CODE_MONTHLY ? t('billedMonthly') : t('billedAnnually')}
        </Typography>
      </div>

      <div className={styles.row}>
        <Typography variant='body2' component='span'>
          {t('totalSeats')}
        </Typography>

        <SeatsCountSelector count={seatsCount} onDecrease={handleDecreaseSeat} onIncrease={handleIncreaseSeat} />
      </div>

      <div className={styles.row}>
        <Typography variant='body2' component='span'>
          {t('discountCode')}
        </Typography>

        {selectedPlan === PLAN_CODE_MONTHLY && (
          <DiscountInput onApply={handleApplyCoupon} onRemove={handleRemoveCoupon} plan={selectedPlan} />
        )}

        {selectedPlan === PLAN_CODE_YEARLY && (
          <DiscountInput onApply={handleApplyCoupon} onRemove={handleRemoveCoupon} plan={selectedPlan} />
        )}
      </div>

      <div className={styles.devider}></div>

      <div className={styles.row}>
        <div className={styles.calculationsRow}>
          <Typography variant='body2' component='span' color='#393534'>
            {t('subtotal')}
          </Typography>

          <Typography variant='body2' component='span' color='#393534'>
            {calculationsSum}
          </Typography>
        </div>

        <div className={styles.calculationsRow}>
          <Typography variant='body2' component='span' color='#393534'>
            {t('discount')}
          </Typography>

          <Typography variant='body2' component='span' color='#393534'>
            -${subscriptionProration?.initialDiscount.toFixed(2)}
          </Typography>
        </div>

        <div className={styles.calculationsRow}>
          <Typography variant='body4' component='span'>
            {t('total')}
          </Typography>

          <Typography variant='body4' component='span'>
            ${subscriptionProration?.initialChargeAmount.toFixed(2)}
          </Typography>
        </div>
      </div>

      <div className={styles.devider}></div>

      <div>
        <Typography variant='body2' component='span' color='#626363'>
          {renewsDate}
        </Typography>
      </div>

      <div className={styles.buttons}>
        <Button variant='contained' onClick={handlePayClick}>
          {t('payAmount', { amount: subscriptionProration?.initialChargeAmount.toFixed(2) })}
        </Button>
      </div>
    </Box>
  );

  return (
    <>
      {loading && <Spinner />}
      <div className={styles.page}>
        <BackButton text={t('selectPlanGoBack')} onClick={handleBackToSubscriptionsClick} />
        {!isMobile && secondAccountPaymentRequired && (
          <div className={styles.containerSplit}>
            {benefitsPanel}
            {detailsPanel}
          </div>
        )}
        {(isMobile || !secondAccountPaymentRequired) && (
          <>
            {isBenefitsOpen && benefitsPanel}
            {!isBenefitsOpen && detailsPanel}
          </>
        )}
      </div>
    </>
  );
}
